import _asyncToGenerator from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _regeneratorRuntime from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it["return"] != null) it["return"](); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import { useCallback, useState } from 'react';
import { EntityName, FormatType, apiDownloadQuoteOrValidationFilerReport, useFileDownload, useLocalTranslation } from '@jove-frontend/uikit';
import { has } from 'lodash';
import { toast } from 'react-hot-toast';

/**
 * Converts URLSearchParams to a plain object
 * Multiple values for the same key are handled appropriately
 * Key arrays are applied differently based on the entity type
 *
 * @param params - The URLSearchParams instance to convert
 * @param entityName - The entity type (e.g., 'quote', 'validation')
 * @returns A plain object representation of the URLSearchParams
 */
export var convertParamsToObject = function convertParamsToObject(params, entityName) {
  var filtersObject = {}; // Define array keys for different entity types

  var quoteArrayKeys = ['region', 'status', 'renew_status', 'insurance_type', 'organization_ids', 'insurer', 'quoting_through', 'quote_creation_methods', 'validation_status', 'referral_assignee_id', 'purchase_probability', 'partner_id', 'assignee_id'];
  var validationArrayKeys = ['status', 'state', 'account_status', 'assignee_names', 'candidate_id', 'candidate_name', 'candidate_country_code', 'organization_names', 'organization_slugs', 'contract_ids', 'region', 'contract_job_country_code', 'sort_column', 'sort_order', 'organization_categories', 'expired_status'];
  var alwaysArrayKeys = entityName === EntityName.VALIDATION ? validationArrayKeys : entityName === EntityName.QUOTE ? quoteArrayKeys : [];

  var _iterator = _createForOfIteratorHelper(params.entries()),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var _step$value = _slicedToArray(_step.value, 2),
          key = _step$value[0],
          value = _step$value[1];

      var processedKey = key; // Some validation keys has different names in the API

      if (entityName === EntityName.VALIDATION) {
        if (key === 'assignee_name') {
          processedKey = 'assignee_names';
        } else if (key === 'organization_name') {
          processedKey = 'organization_names';
        } else if (key === 'organization_slug') {
          processedKey = 'organization_slugs';
        }
      } else if (entityName === EntityName.QUOTE) {
        if (key === 'organization_id') {
          processedKey = 'organization_ids';
        }
      }

      if (filtersObject[processedKey]) {
        if (!Array.isArray(filtersObject[processedKey])) {
          filtersObject[processedKey] = [filtersObject[processedKey]];
        }

        filtersObject[processedKey].push(value);
      } else if (alwaysArrayKeys.includes(processedKey)) {
        filtersObject[processedKey] = [value];
      } else {
        filtersObject[processedKey] = value;
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  if (entityName === EntityName.QUOTE) {
    // Special handling due to API issues
    if (has(filtersObject, 'partner_id')) {
      filtersObject['partner_ids'] = filtersObject['partner_id'];
      delete filtersObject['partner_id'];
    }
  }

  return filtersObject;
};
/**
 * Custom hook to handle file report generation and downloading
 *
 * @returns An object containing functions and states for file report operations
 */

export var useFileReport = function useFileReport() {
  var _useState = useState(false),
      isGeneratingReport = _useState[0],
      setIsGeneratingReport = _useState[1];

  var _useLocalTranslation = useLocalTranslation(),
      $t = _useLocalTranslation.$t;

  var _useFileDownload = useFileDownload(),
      downloadBlobFile = _useFileDownload.downloadBlobFile;
  /**
   * Prepares filter settings from URLSearchParams
   *
   * @param entityName - The entity type (e.g., 'quote')
   * @param params - The URLSearchParams instance with filter criteria
   * @returns An object with the proper filter settings structure for the API
   */


  var prepareFilterSettings = useCallback(function (entityName, params) {
    var filtersObject = convertParamsToObject(params, entityName); // Create the appropriate filter settings structure based on entity type

    var filterSettings = {};

    if (entityName === EntityName.QUOTE) {
      filterSettings['quote_filters'] = filtersObject;
    } else if (entityName === EntityName.VALIDATION) {
      filterSettings['validation_filters'] = filtersObject;
    } else {
      filterSettings["".concat(entityName, "_filters")] = filtersObject;
    }

    return filterSettings;
  }, []);
  /**
   * Generates a file report and downloads it when ready
   *
   * @param options - Options for generating the file report
   * @param options.entityName - Type of entity to generate report for (e.g., 'quote')
   * @param options.formatType - Format of the report (e.g., 'csv')
   * @param options.filterSettings - Filters to apply to the report (can be either an object or URLSearchParams)
   * @param options.filename - Optional custom filename (without extension)
   * @returns Promise that resolves when the report is downloaded or rejects on error
   */

  var generateAndDownloadReport = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
      var entityName, _ref$formatType, formatType, filterSettings, filename, finalFilterSettings, payload, _yield$apiDownloadQuo, _yield$apiDownloadQuo2, res;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              entityName = _ref.entityName, _ref$formatType = _ref.formatType, formatType = _ref$formatType === void 0 ? FormatType.CSV : _ref$formatType, filterSettings = _ref.filterSettings, filename = _ref.filename;
              _context.prev = 1;
              setIsGeneratingReport(true);
              finalFilterSettings = filterSettings instanceof URLSearchParams ? prepareFilterSettings(entityName, filterSettings) : filterSettings;

              if (entityName === EntityName.QUOTE) {
                payload = {
                  entity_name: entityName,
                  format_type: formatType,
                  filter_settings: {
                    quote_filters: finalFilterSettings instanceof URLSearchParams ? finalFilterSettings : finalFilterSettings['quote_filters'] || finalFilterSettings
                  }
                };
              } else if (entityName === EntityName.VALIDATION) {
                payload = {
                  entity_name: entityName,
                  format_type: formatType,
                  filter_settings: {
                    validation_filters: finalFilterSettings instanceof URLSearchParams ? {} : finalFilterSettings['validation_filters'] || finalFilterSettings
                  }
                };
              } else {
                payload = {
                  entity_name: entityName,
                  format_type: formatType,
                  filter_settings: {
                    quote_filters: finalFilterSettings instanceof URLSearchParams ? {} : finalFilterSettings['quote_filters'] || finalFilterSettings
                  }
                };
              }

              _context.next = 7;
              return apiDownloadQuoteOrValidationFilerReport(payload);

            case 7:
              _yield$apiDownloadQuo = _context.sent;
              _yield$apiDownloadQuo2 = _slicedToArray(_yield$apiDownloadQuo, 1);
              res = _yield$apiDownloadQuo2[0];

              if (!res) {
                _context.next = 16;
                break;
              }

              setIsGeneratingReport(false);
              toast.success('Download successfully!');
              downloadBlobFile(res);
              _context.next = 18;
              break;

            case 16:
              toast.error($t('pages:quotes.file_report_errors.generation_failed'));
              throw new Error('Failed to generate file report');

            case 18:
              _context.next = 25;
              break;

            case 20:
              _context.prev = 20;
              _context.t0 = _context["catch"](1);
              setIsGeneratingReport(false);
              toast.error($t('pages:quotes.file_report_errors.unknown_error', {
                message: _context.t0.message || 'Unknown error'
              }));
              throw _context.t0;

            case 25:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[1, 20]]);
    }));

    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [prepareFilterSettings, $t]); // Helper function to detect Safari browser

  var isSafariBrowser = function isSafariBrowser() {
    var ua = navigator.userAgent.toLowerCase();
    return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
  };

  return {
    isGeneratingReport: isGeneratingReport,
    generateAndDownloadReport: generateAndDownloadReport,
    convertParamsToObject: convertParamsToObject
  };
};